import { sectionRenderer } from '@theme/section-renderer';

const originalFetch = window.fetch.bind(window);
const root = window.Shopify?.routes?.root || '/';

let running = null;
let repeat = false;
let timer;
let selectedChoice = null;
let externalMutations = 0;

function getPicker() {
  return document.querySelector('[data-ocean-cutting]');
}

async function cartRequest(endpoint, body) {
  const response = await originalFetch(root + endpoint, {
    method: body ? 'POST' : 'GET',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  const data = await response.json();

  if (!response.ok || data.status) {
    throw new Error(
      data.description ||
      data.message ||
      'Could not update cart charges.'
    );
  }

  return data;
}

function showError(message) {
  document.querySelectorAll('[data-cutting-error]').forEach(
    (element) => {
      element.textContent = message;
    }
  );
}

async function setServiceQuantity(variantId, quantity, property) {
  let cart = await cartRequest('cart.js');

  let lines = cart.items.filter(
    (item) => Number(item.variant_id) === variantId
  );

  // Remove duplicate service lines.
  while (lines.length > 1) {
    await cartRequest('cart/change.js', {
      id: lines[lines.length - 1].key,
      quantity: 0,
    });

    cart = await cartRequest('cart.js');

    lines = cart.items.filter(
      (item) => Number(item.variant_id) === variantId
    );
  }

  const existingLine = lines[0];

  if (existingLine) {
    if (existingLine.quantity !== quantity) {
      await cartRequest('cart/change.js', {
        id: existingLine.key,
        quantity,
      });
    }
  } else if (quantity > 0) {
    await cartRequest('cart/add.js', {
      items: [{
        id: variantId,
        quantity,
        properties: {
          [property]: 'true',
        },
      }],
    });
  }
}

async function synchronize() {
  const picker = getPicker();
  if (!picker) return;

  const cuttingId = Number(picker.dataset.serviceVariantId);

  const deliveryElement = document.querySelector(
    '[data-ocean-delivery]'
  );

  const deliveryId = Number(
    deliveryElement?.dataset.deliveryVariantId
  );

  if (!cuttingId) {
    throw new Error('Fish Cutting Service could not be found.');
  }

  if (!deliveryId || deliveryId === cuttingId) {
    throw new Error(
      'Delivery Charge product could not be found. Check its handle is delivery-charge and it is available on Online Store.'
    );
  }

  const cart = await cartRequest('cart.js');

  const choice =
    selectedChoice ??
    (cart.attributes?.['Fish cutting'] === 'With cutting'
      ? 'With cutting'
      : 'Without cutting');

  selectedChoice = choice;

  // Exclude cutting and delivery from the actual products.
  const products = cart.items.filter((item) => {
    const id = Number(item.variant_id);

    return id !== cuttingId && id !== deliveryId;
  });

  // Existing store logic: one fish quantity represents 1 kg.
  const kilograms = products.reduce((total, item) => {
    return item.requires_shipping
      ? total + item.quantity
      : total;
  }, 0);

  const cuttingQuantity =
    choice === 'With cutting' ? kilograms : 0;

  // Delivery is added once per order.
  const deliveryQuantity = products.length > 0 ? 1 : 0;

  await setServiceQuantity(
    cuttingId,
    cuttingQuantity,
    '_ocean_cart_cutting'
  );

  await setServiceQuantity(
    deliveryId,
    deliveryQuantity,
    '_ocean_delivery_charge'
  );

  if (cart.attributes?.['Fish cutting'] !== choice) {
    await cartRequest('cart/update.js', {
      attributes: {
        'Fish cutting': choice,
      },
    });
  }

  // Refresh the cart display with Shopify's updated total.
  const sectionIds = new Set(
    [...document.querySelectorAll(
      'cart-items-component[data-section-id]'
    )]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean)
  );

  for (const sectionId of sectionIds) {
    await sectionRenderer.renderSection(sectionId, {
      cache: false,
      mode: sectionId === 'cart-drawer-section'
        ? 'hydration'
        : 'full',
    });
  }

  showError('');
}

function requestSync() {
  repeat = true;

  if (running) return running;

  running = (async () => {
    do {
      repeat = false;

      while (externalMutations > 0) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      await synchronize();
    } while (repeat);
  })().finally(() => {
    running = null;
  });

  return running;
}

function scheduleSync() {
  clearTimeout(timer);

  timer = setTimeout(() => {
    requestSync().catch((error) => {
      console.error('[ocean-cart-charges]', error);
      showError(error.message);
    });
  }, 150);
}

document.addEventListener('change', (event) => {
  const input = event.target;

  if (!input.matches?.('[data-cutting-choice]')) return;

  selectedChoice = input.value;
  scheduleSync();
});

// Synchronize after products are added, changed, or removed.
window.fetch = async function (input, init) {
  const url = typeof input === 'string'
    ? input
    : input instanceof URL
      ? input.href
      : input?.url || '';

  const method = String(
    init?.method || input?.method || 'GET'
  ).toUpperCase();

  const isMutation =
    method !== 'GET' &&
    /\/cart\/(add|change|update|clear)(\.js)?(?:[?#]|$)/i.test(url);

  if (!isMutation) {
    return originalFetch(input, init);
  }

  externalMutations++;

  try {
    const response = await originalFetch(input, init);

    if (response.ok) scheduleSync();

    return response;
  } finally {
    externalMutations--;
  }
};

// Complete charge synchronization before checkout.
document.addEventListener('click', async (event) => {
  const checkoutButton = event.target.closest?.(
    'button[name="checkout"], input[name="checkout"], a[href*="/checkout"]'
  );

  if (!checkoutButton || !getPicker()) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  clearTimeout(timer);

  try {
    await requestSync();
    window.location.assign(root + 'checkout');
  } catch (error) {
    console.error('[ocean-cart-charges]', error);
    showError(error.message);
  }
}, true);

scheduleSync();